import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

/**
 * ⚖️ FAR 117 COMPLIANCE PANEL - THE BADASS VISUAL
 * 
 * Rethinking FAR 117 display with modern visual timeline:
 * - Color-coded duty bars (green/amber/red)
 * - Visual rest indicators between days
 * - Rolling limits with progress rings
 * - Clear violation alerts
 * - Expandable duty period details
 * 
 * Mobile: Scrollable horizontal timeline
 * Desktop: Vertical panel sidebar
 */

const CompliancePanel = ({ compliance }) => {
  const [expandedDuty, setExpandedDuty] = useState(null);

  if (!compliance) {
    return (
      <div className="card p-6 text-center text-muted-foreground">
        <Icon name="AlertCircle" className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>No compliance data available</p>
      </div>
    );
  }

  const {
    is_compliant,
    violations = [],
    duty_periods = [],
    timeline_visual = {}
  } = compliance;

  const { duty_bars = [], rest_indicators = [] } = timeline_visual;

  const toggleDuty = (index) => {
    setExpandedDuty(expandedDuty === index ? null : index);
  };

  // Calculate duty bar color based on utilization
  const getDutyBarColor = (utilization) => {
    if (utilization >= 100) return 'bg-error'; // Red: Over limit
    if (utilization >= 80) return 'bg-warning'; // Amber: Warning zone
    return 'bg-success'; // Green: Safe
  };

  const getDutyBarTextColor = (utilization) => {
    if (utilization >= 100) return 'text-error';
    if (utilization >= 80) return 'text-warning';
    return 'text-success';
  };

  // Get compliance badge
  const complianceBadge = is_compliant ? (
    <div className="flex items-center gap-2 text-success">
      <div className="w-8 h-8 rounded-full bg-success/20 flex items-center justify-center">
        <Icon name="CheckCircle" className="w-5 h-5" />
      </div>
      <span className="font-semibold">Legal</span>
    </div>
  ) : (
    <div className="flex items-center gap-2 text-error">
      <div className="w-8 h-8 rounded-full bg-error/20 flex items-center justify-center">
        <Icon name="AlertTriangle" className="w-5 h-5" />
      </div>
      <span className="font-semibold">{violations.length} Violation{violations.length !== 1 ? 's' : ''}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header Card */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-foreground">FAR 117 Compliance</h3>
          {complianceBadge}
        </div>

        {/* Violations (if any) */}
        {violations.length > 0 && (
          <div className="space-y-2 mt-4">
            {violations.map((violation, idx) => (
              <div key={idx} className="p-3 rounded-lg bg-error/10 border border-error/30">
                <div className="flex items-start gap-2">
                  <Icon name="AlertTriangle" className="w-4 h-4 text-error mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-error">
                      {violation.violation_type}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {violation.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Visual Timeline */}
      <div className="card p-4">
        <h4 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
          <Icon name="BarChart3" className="w-4 h-4" />
          DUTY PERIOD TIMELINE
        </h4>

        <div className="space-y-4">
          {duty_bars.map((bar, index) => {
            const isExpanded = expandedDuty === index;
            const barColor = getDutyBarColor(bar.utilization_percent);
            const textColor = getDutyBarTextColor(bar.utilization_percent);

            return (
              <div key={index}>
                {/* Duty Bar */}
                <button
                  onClick={() => toggleDuty(index)}
                  className="w-full text-left hover:opacity-80 transition-opacity"
                >
                  {/* Day Label */}
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      Day {bar.day} • {bar.date}
                    </span>
                    <span className={`text-xs font-bold ${textColor}`}>
                      {bar.duty_hours}h / {bar.limit_hours}h
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="relative h-8 rounded-lg bg-muted overflow-hidden">
                    {/* Fill */}
                    <div
                      className={`absolute inset-y-0 left-0 ${barColor} transition-all duration-500`}
                      style={{ width: `${Math.min(bar.utilization_percent, 100)}%` }}
                    />
                    
                    {/* Over-limit indicator (if exceeded) */}
                    {bar.utilization_percent > 100 && (
                      <div
                        className="absolute inset-y-0 bg-error/40 animate-pulse"
                        style={{ 
                          left: '100%', 
                          width: `${bar.utilization_percent - 100}%`,
                          transform: 'translateX(-100%)'
                        }}
                      />
                    )}

                    {/* Label */}
                    <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-medium">
                      <span className="text-foreground/80">
                        {bar.flight_count} flights
                      </span>
                      <span className={textColor}>
                        {bar.utilization_percent}%
                      </span>
                    </div>
                  </div>
                </button>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="mt-3 p-3 rounded-lg bg-muted/50 text-sm space-y-2">
                    <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                      <div>
                        <div className="text-xs text-muted-foreground">Report</div>
                        <div className="font-medium">{bar.report_time || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Release</div>
                        <div className="font-medium">{bar.release_time || 'N/A'}</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Block</div>
                        <div className="font-medium">{bar.block_hours || 0}h</div>
                      </div>
                      <div>
                        <div className="text-xs text-muted-foreground">Credit</div>
                        <div className="font-medium">{bar.credit_hours || 0}h</div>
                      </div>
                    </div>

                    {/* Regulation Reference */}
                    {bar.regulation_section && (
                      <div className="pt-2 border-t border-border text-xs text-muted-foreground">
                        <Icon name="BookOpen" className="w-3 h-3 inline mr-1" />
                        {bar.regulation_section}
                      </div>
                    )}
                  </div>
                )}

                {/* Rest Indicator (if exists for this day) */}
                {rest_indicators[index] && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <div className="h-px flex-1 bg-border" />
                    <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-muted">
                      <Icon name="Moon" className="w-3 h-3" />
                      <span>{rest_indicators[index].rest_hours}h rest</span>
                    </div>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Rolling Limits */}
      <div className="card p-4">
        <h4 className="text-sm font-semibold text-muted-foreground mb-4 flex items-center gap-2">
          <Icon name="TrendingUp" className="w-4 h-4" />
          ROLLING LIMITS
        </h4>

        <div className="space-y-4">
          {/* 7-Day Limit */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">60 in 168 hours (7 days)</span>
              <span className="text-sm font-bold text-foreground">
                {compliance.rolling_7day || 0}h / 60h
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  (compliance.rolling_7day || 0) > 60 ? 'bg-error' : 
                  (compliance.rolling_7day || 0) > 48 ? 'bg-warning' : 
                  'bg-success'
                }`}
                style={{ width: `${Math.min(((compliance.rolling_7day || 0) / 60) * 100, 100)}%` }}
              />
            </div>
          </div>

          {/* 28-Day Limit */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">190 in 672 hours (28 days)</span>
              <span className="text-sm font-bold text-foreground">
                {compliance.rolling_28day || 0}h / 190h
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  (compliance.rolling_28day || 0) > 190 ? 'bg-error' : 
                  (compliance.rolling_28day || 0) > 152 ? 'bg-warning' : 
                  'bg-success'
                }`}
                style={{ width: `${Math.min(((compliance.rolling_28day || 0) / 190) * 100, 100)}%` }}
              />
            </div>
          </div>

          {/* Annual Limit */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">1000 in 365 days (annual)</span>
              <span className="text-sm font-bold text-foreground">
                {compliance.rolling_365day || 0}h / 1000h
              </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ${
                  (compliance.rolling_365day || 0) > 1000 ? 'bg-error' : 
                  (compliance.rolling_365day || 0) > 800 ? 'bg-warning' : 
                  'bg-success'
                }`}
                style={{ width: `${Math.min(((compliance.rolling_365day || 0) / 1000) * 100, 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="card p-4 bg-warning/10 border border-warning/30">
        <div className="flex items-start gap-2 text-xs text-muted-foreground">
          <Icon name="Info" className="w-4 h-4 flex-shrink-0 mt-0.5 text-warning" />
          <p>
            <strong className="text-warning">Disclaimer:</strong> FAR 117 predictions are estimates and not guarantees. 
            Always verify compliance with your airline's scheduling department.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CompliancePanel;
